import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, ChevronRight, CheckCircle, Building2, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const technologies = [
  'React', 'Node.js', 'Python', 'AWS', 'Azure', 'Docker', 
  'Kubernetes', 'TypeScript', 'Java', '.NET', 'PostgreSQL', 
  'MongoDB', 'GraphQL', 'Flutter'
];

// Custom Code Icon component - < / > style with spacing
const CodeIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="8 6 2 12 8 18" />
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="16 6 22 12 16 18" />
  </svg>
);

export const Home = () => {
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/services`),
        ]);
        setStats(statsRes.data);
        setServices(servicesRes.data.slice(0, 2));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f1a]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/28428592/pexels-photo-28428592.jpeg"
            alt="Technology background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1a] via-[#0a0f1a]/90 to-[#0a0f1a]/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Hero Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="badge-orange">
                IT Recruitment & Software Services
              </div>
              
              <h1 className="heading-1" data-testid="hero-title">
                Building Teams.
                <span className="block whitespace-nowrap">
                  <span className="text-[#0088CC]">Delivering</span>{' '}
                  <span className="text-[#FF6600]">Solutions.</span>
                </span>
              </h1>
              
              <p className="text-lg text-slate-400 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                Sharebox Technologies connects exceptional talent with innovative companies 
                while delivering cutting-edge software solutions that transform businesses.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/services">
                  <Button 
                    className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
                    data-testid="hero-cta-services"
                  >
                    Explore Services
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/careers">
                  <Button 
                    variant="outline"
                    className="border-[#1e293b] text-white hover:bg-[#111827] font-medium px-8 py-6 text-base rounded-lg"
                    data-testid="hero-cta-careers"
                  >
                    View Open Roles
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block animate-fade-in-up stagger-2">
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/28428592/pexels-photo-28428592.jpeg"
                  alt="Abstract Technology"
                  className="w-full aspect-square object-cover rounded-2xl"
                />
              </div>
              {/* Floating icons with animation */}
              <div className="absolute -top-4 -right-4 w-14 h-14 rounded-xl bg-[#111827] border border-[#0088CC]/50 flex items-center justify-center animate-bounce-slow">
                <Users className="text-[#0088CC]" size={24} />
              </div>
              <div className="absolute -bottom-4 -left-4 w-14 h-14 rounded-xl bg-[#111827] border border-[#FF6600]/50 flex items-center justify-center animate-bounce-slow-delayed">
                <CodeIcon size={24} className="text-[#FF6600]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section with gradient background */}
      <section className="py-20 border-y border-[#1e293b] relative overflow-hidden" data-testid="stats-section">
        {/* Background gradient effects - corner glows */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Blue glow from bottom-left */}
          <div 
            className="absolute bottom-0 left-0 w-[500px] h-[400px]"
            style={{
              background: 'radial-gradient(ellipse at bottom left, rgba(0, 136, 204, 0.25) 0%, rgba(0, 136, 204, 0.1) 30%, transparent 70%)'
            }}
          />
          {/* Orange glow from bottom-right */}
          <div 
            className="absolute bottom-0 right-0 w-[500px] h-[400px]"
            style={{
              background: 'radial-gradient(ellipse at bottom right, rgba(255, 102, 0, 0.25) 0%, rgba(255, 102, 0, 0.1) 30%, transparent 70%)'
            }}
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#111827] border border-[#0088CC]/30 flex items-center justify-center">
                <Users className="text-[#0088CC]" size={28} />
              </div>
              <div className="stat-number text-[#0088CC]">500+</div>
              <div className="text-slate-500 text-sm mt-1">Placements Made</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#111827] border border-[#0088CC]/30 flex items-center justify-center">
                <Building2 className="text-[#0088CC]" size={28} />
              </div>
              <div className="stat-number text-[#0088CC]">100+</div>
              <div className="text-slate-500 text-sm mt-1">Client Companies</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#111827] border border-[#0088CC]/30 flex items-center justify-center">
                <CodeIcon size={28} className="text-[#0088CC]" />
              </div>
              <div className="stat-number text-[#0088CC]">150+</div>
              <div className="text-slate-500 text-sm mt-1">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#111827] border border-[#0088CC]/30 flex items-center justify-center">
                <UserCheck className="text-[#0088CC]" size={28} />
              </div>
              <div className="stat-number text-[#0088CC]">98%</div>
              <div className="text-slate-500 text-sm mt-1">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
              What We Do
            </span>
            <h2 className="heading-2 mb-6">Our Core Services</h2>
            <p className="body-text">
              From finding the right talent to building the right solutions, we cover all your technology needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* IT Recruitment Card - Blue gradient */}
            <div 
              className="relative rounded-2xl p-8 overflow-hidden group transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 136, 204, 0.15) 0%, rgba(10, 15, 26, 0.9) 50%, rgba(10, 15, 26, 1) 100%)',
                border: '1px solid rgba(0, 136, 204, 0.3)'
              }}
              data-testid="service-card-recruitment"
            >
              {/* Glowing left border */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0088CC] via-[#0088CC]/50 to-transparent" />
              
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0088CC]/30 to-[#0088CC]/10 border border-[#0088CC]/30 flex items-center justify-center mb-6">
                <Users className="text-[#0088CC]" size={24} />
              </div>
              <h3 className="heading-3 mb-4">IT Recruitment</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Connect with top-tier tech talent. We specialize in finding the perfect 
                candidates for your technical roles.
              </p>
              <ul className="space-y-3 mb-6">
                {['Permanent Placements', 'Contract Staffing', 'Executive Search', 'RPO Services'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-400">
                    <CheckCircle size={16} className="text-[#0088CC]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/services" 
                className="inline-flex items-center text-[#0088CC] font-medium text-sm group-hover:gap-2 transition-all"
              >
                Learn More <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>

            {/* Software Development Card - Orange gradient */}
            <div 
              className="relative rounded-2xl p-8 overflow-hidden group transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 102, 0, 0.15) 0%, rgba(10, 15, 26, 0.9) 50%, rgba(10, 15, 26, 1) 100%)',
                border: '1px solid rgba(255, 102, 0, 0.3)'
              }}
              data-testid="service-card-software"
            >
              {/* Glowing left border */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FF6600] via-[#FF6600]/50 to-transparent" />
              
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6600]/30 to-[#FF6600]/10 border border-[#FF6600]/30 flex items-center justify-center mb-6">
                <CodeIcon size={24} className="text-[#FF6600]" />
              </div>
              <h3 className="heading-3 mb-4">Software Development</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Custom software solutions built with cutting-edge technologies 
                to drive your business forward.
              </p>
              <ul className="space-y-3 mb-6">
                {['Web Applications', 'Mobile Apps', 'Cloud Solutions', 'API Development'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-400">
                    <CheckCircle size={16} className="text-[#FF6600]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/services" 
                className="inline-flex items-center text-[#FF6600] font-medium text-sm group-hover:gap-2 transition-all"
              >
                Learn More <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Marquee with gradient badges */}
      <section className="py-16 border-y border-[#1e293b] overflow-hidden" data-testid="tech-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 text-center">
          <span className="text-slate-500 text-sm font-medium tracking-wider uppercase">
            Technologies We Work With
          </span>
        </div>
        <div className="relative px-6 marquee-container">
          <div className="flex gap-4 animate-marquee">
            {[...technologies, ...technologies].map((tech, index) => (
              <div 
                key={index}
                className={`tech-badge-gradient whitespace-nowrap cursor-pointer ${index % 2 === 0 ? 'hover-blue' : 'hover-orange'}`}
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-2 mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="body-text mb-8 max-w-2xl mx-auto">
            Whether you need top tech talent or custom software solutions, 
            we're here to help you succeed.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
                data-testid="cta-contact-btn"
              >
                Contact Us Today
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button 
                variant="outline"
                className="border-[#1e293b] text-white hover:bg-[#111827] font-medium px-8 py-6 text-base rounded-lg"
                data-testid="cta-portfolio-btn"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
